// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import GazeGraph from "src/components/containers/shared/GazeGraph/GazeGraph";
import EstermanChart from "src/components/containers/shared/ReportSections/ReportGraph/EstermanGraph/EstermanChart/EstermanChart";
import Full120Chart from "src/components/containers/shared/ReportSections/ReportGraph/Full120Graph/Full120Chart/Full120Chart";
import Superior36Chart from "src/components/containers/shared/ReportSections/ReportGraph/Superior36Graph/Superior36Chart/Superior36Chart";
import VFScreeningChart from "src/components/containers/shared/ReportSections/ReportGraph/VFScreeningGraph/VFScreeningChart/VFScreeningChart";
import NumberGraph from "src/components/containers/shared/ReportSections/ReportGraph/VFThresholdingGraph/VFThresholdingChart/NumberGraph/NumberGraph";
import { WarningIcon } from "src/assets/icons";

// Constants
import {
  examCardEnums,
  eyeTypeEnums,
  gridTypeEnums,
} from "src/constants/enums";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useExamReportStats from "src/hooks/reports/useExamReportStats";

// Utils
import {
  getChartSpots,
  getGazeChartData,
} from "src/utils/exams/examReportsHelper";

const DESKTOP_CHART_SIZE = 350;

const VFGraphs = () => {
  const { t } = useTranslation();

  const {
    exam,
    currentExamSection,
    isConfirmation,
    isNotStarted,
    inTutorial,
    inCalibration,
    examReportLive,
    isVisualField,
    isEsterman,
    isVfScreening,
    isFull120,
    isSuperior36,
  } = useLiveUpdateContext();

  const { LEFT, RIGHT } = eyeTypeEnums;

  const { fixationLossPercentage, falsePositivePercentage, falseNegative } =
    useExamReportStats(examReportLive);

  const numberGraphContainerSize = useMemo(() => DESKTOP_CHART_SIZE / 0.9, []);
  const wideChartContainerSize = useMemo(() => DESKTOP_CHART_SIZE / 0.7, []);

  const showVisualFieldChart = useMemo(
    () =>
      isVisualField &&
      examReportLive &&
      exam &&
      !isConfirmation &&
      !isNotStarted &&
      !inTutorial &&
      !inCalibration,
    [
      exam,
      examReportLive,
      inCalibration,
      inTutorial,
      isConfirmation,
      isNotStarted,
      isVisualField,
    ]
  );

  const showEstermanChart = useMemo(
    () =>
      isEsterman &&
      examReportLive &&
      exam &&
      !isConfirmation &&
      !isNotStarted &&
      !inTutorial &&
      !inCalibration,
    [
      exam,
      examReportLive,
      inCalibration,
      inTutorial,
      isConfirmation,
      isNotStarted,
      isEsterman,
    ]
  );

  // the eye that the live update modal refers to (Left or Right)
  // the logic is:
  // - if there is only one section, then simply get the eye that it is related to
  // - if there are two sections:
  //    - if right eye is not completed, then consider the right eye as the current one
  //    - if right eye is completed, then consider the left eye as the current one
  //    - to check if the section is completed, we should check if the completionDate field is null
  const eyeType = useMemo(() => {
    if (!exam) return "";

    // get the current exam section being tested
    const enumValues = Object.values(examCardEnums);
    const currentSectionName = enumValues.find(
      (value) => value.type === currentExamSection?.type
    )?.sectionName;

    if (!currentSectionName) return "";
    if (!exam?.[currentSectionName]?.length) return "";

    const currentGroupId = currentExamSection?.groupId;

    const rightSection = exam[currentSectionName]?.find(
      (section) => section.groupId === currentGroupId && section.eye === RIGHT
    );
    const leftSection = exam[currentSectionName]?.find(
      (section) => section.groupId === currentGroupId && section.eye === LEFT
    );

    if (!rightSection && !leftSection) return "";

    return !rightSection ||
      (leftSection?.order === 0 && !leftSection?.completionDate) ||
      (rightSection?.order === 0 && rightSection?.completionDate)
      ? LEFT
      : RIGHT;
  }, [exam, currentExamSection, RIGHT, LEFT]);

  const visualFieldChartData = useMemo(() => {
    // return if the chart is not visual field/esterman
    if (!(isVisualField || isEsterman)) return [];

    return getChartSpots(examReportLive) ?? [];
  }, [examReportLive, isVisualField, isEsterman]);

  const gazeChartData = useMemo(() => {
    // return if the chart is not visual field/esterman
    if (!(isVisualField || isEsterman)) return [];

    const { gazeSpots } = getGazeChartData(examReportLive);
    return gazeSpots ?? [];
  }, [isVisualField, isEsterman, examReportLive]);

  const isHighFalsePos = useMemo(() => {
    if (!/^\d+(\.\d+)?%$/.test(falsePositivePercentage ?? "")) return false;

    return parseFloat(falsePositivePercentage ?? "") > 25;
  }, [falsePositivePercentage]);

  const isHighFalseNeg = useMemo(() => {
    if (!/^\d+\/\d+$/.test(falseNegative ?? "")) return false;

    const [numerator, denominator] = (falseNegative ?? "")
      .split("/")
      .map(Number);

    return numerator / denominator > 0.25;
  }, [falseNegative]);

  const isHighFixationLoss = useMemo(() => {
    if (/^\d+(\.\d+)?%$/.test(fixationLossPercentage ?? "")) {
      return parseFloat(fixationLossPercentage ?? "") > 25;
    }

    if (/^\d+\/\d+$/.test(fixationLossPercentage ?? "")) {
      const [numerator, denominator] = (fixationLossPercentage ?? "")
        .split("/")
        .map(Number);
      return numerator / denominator > 0.25;
    }

    return false;
  }, [fixationLossPercentage]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="16px"
      width="100%"
      height="100%"
    >
      {!isConfirmation && !isNotStarted && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography fontSize="12px" textAlign="center" fontWeight={600}>
            {t("Eye Tracking")}
          </Typography>
          <GazeGraph gazeSpots={gazeChartData} isLiveUpdate />
        </Box>
      )}

      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="center"
        gap="16px"
        width="100%"
        flex={1}
        minHeight={0}
      >
        <Box
          sx={{
            display: "flex",
            minWidth: { xs: "100%", md: "80%" },
            maxWidth: "100%",
            flex: 0,
            p: "4px 8px",
            justifyContent: "center",
            alignItems: "center",
            height: "auto",
          }}
        >
          {/* VF thresholding graph */}
          {showVisualFieldChart &&
            !isVfScreening &&
            !isFull120 &&
            !isSuperior36 && (
              <NumberGraph
                points={visualFieldChartData}
                gridType={currentExamSection?.gridType}
                displayTriangle={
                  currentExamSection?.gridType !== gridTypeEnums.G_10_2.type
                }
                eyeType={eyeType}
                isLiveUpdate
                containerSize={numberGraphContainerSize}
              />
            )}

          {/* VF screening graph */}
          {showVisualFieldChart && isVfScreening && (
            <VFScreeningChart
              vfScreeningSpots={visualFieldChartData}
              gridType={currentExamSection?.gridType}
              eyeType={eyeType}
              isLiveUpdate
              containerSize={wideChartContainerSize}
            />
          )}

          {showVisualFieldChart && isSuperior36 && (
            <Superior36Chart
              superior36Spots={visualFieldChartData}
              isLiveUpdate
              containerSize={wideChartContainerSize}
            />
          )}

          {showVisualFieldChart && isFull120 && (
            <Full120Chart
              full120Spots={visualFieldChartData}
              isLiveUpdate
              containerSize={wideChartContainerSize}
            />
          )}

          {showEstermanChart && (
            <EstermanChart
              estermanSpots={visualFieldChartData}
              isLiveUpdate
              containerSize={wideChartContainerSize}
            />
          )}
        </Box>

        <Box
          display="flex"
          height="fit-content"
          flexDirection={{ xs: "row", md: "column" }}
          width="100%"
          gap="8px"
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              justifyContent: "center",
              maxHeight: "fit-content",
              alignItems: "center",
              backgroundColor: isHighFalsePos ? "#E10C0C10" : "#5B6D7A20",
              border: isHighFalsePos
                ? "1px solid #E10C0C"
                : "1px solid #5B6D7A90",
              color: isHighFalsePos ? "#E10C0C" : "#000",
              borderRadius: "4px",
              p: "8px",
              textAlign: "center",
            }}
          >
            <Typography fontSize="12px">{t("False Positives")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {isHighFalsePos && <WarningIcon height="16px" width="16px" />}
              <Typography fontSize="14px" fontWeight={600}>
                {falsePositivePercentage}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              maxHeight: "fit-content",
              backgroundColor: isHighFalseNeg ? "#E10C0C10" : "#5B6D7A20",
              border: isHighFalseNeg
                ? "1px solid #E10C0C"
                : "1px solid #5B6D7A90",
              color: isHighFalseNeg ? "#E10C0C" : "#000",
              borderRadius: "4px",
              p: "8px",
              textAlign: "center",
            }}
          >
            <Typography fontSize="12px">{t("False Negatives")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {isHighFalseNeg && <WarningIcon height="16px" width="16px" />}
              <Typography fontSize="14px" fontWeight={600}>
                {falseNegative}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              maxHeight: "fit-content",
              backgroundColor: isHighFixationLoss ? "#E10C0C10" : "#5B6D7A20",
              border: isHighFixationLoss
                ? "1px solid #E10C0C"
                : "1px solid #5B6D7A90",
              color: isHighFixationLoss ? "#E10C0C" : "#000",
              borderRadius: "4px",
              p: "8px",
              textAlign: "center",
            }}
          >
            <Typography fontSize="12px">{t("Fixation Loss")}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {isHighFixationLoss && <WarningIcon height="16px" width="16px" />}
              <Typography fontSize="14px" fontWeight={600}>
                {fixationLossPercentage}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VFGraphs;
